import { errorResponseSchema, type ErrorCode, type ErrorDetail } from '@avrahami/contracts';
import type { z } from 'zod';

/** A failed API call, carrying the stable error code from the shared envelope. */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: readonly ErrorDetail[];

  constructor(code: ErrorCode, status: number, message: string, details: readonly ErrorDetail[]) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export function isApiError(error: unknown, code?: ErrorCode): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiClient {
  request<Schema extends z.ZodType>(
    method: Method,
    path: string,
    schema: Schema,
    body?: unknown,
  ): Promise<z.output<Schema>>;
  /** For endpoints that answer 204 No Content (or whose answer the caller does not need). */
  send(method: Method, path: string, body?: unknown): Promise<void>;
  /** POSTs a file as the raw request body, with its own content type (image uploads). */
  upload<Schema extends z.ZodType>(
    path: string,
    file: Blob,
    schema: Schema,
  ): Promise<z.output<Schema>>;
  /** GETs a file (a PDF, for example); failures are ApiErrors like any other call. */
  blob(path: string): Promise<Blob>;
}

export interface ApiClientOptions {
  /** The fetch to use (tests pass a fake). */
  fetch?: typeof fetch;
  /** Extra headers for a request path (the tenant admin names the store it believes active). */
  headers?: (path: string) => Readonly<Record<string, string>>;
}

async function failure(response: Response): Promise<ApiError> {
  const parsed = errorResponseSchema.safeParse(await response.json().catch(() => null));
  if (parsed.success) {
    const { code, message, details } = parsed.data.error;
    return new ApiError(code, response.status, message, details ?? []);
  }
  // Not our envelope (a proxy error page, for example).
  return new ApiError(
    response.status >= 500 ? 'SERVICE_UNAVAILABLE' : 'BAD_REQUEST',
    response.status,
    `HTTP ${String(response.status)}`,
    [],
  );
}

interface Payload {
  body: BodyInit;
  contentType: string;
}

function jsonPayload(body: unknown): Payload | undefined {
  return body === undefined
    ? undefined
    : { body: JSON.stringify(body), contentType: 'application/json' };
}

/**
 * The API lives on the same origin (`/v1/*`), so the session cookie travels by itself and the
 * browser adds the `Origin` header the API checks against cross-site requests. Responses are
 * validated against the shared contracts, so a drifted API fails loudly instead of rendering
 * wrong data.
 */
export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const fetchImpl = options.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  async function call(method: Method, path: string, payload: Payload | undefined) {
    const headers: Record<string, string> = { ...options.headers?.(path) };
    if (payload !== undefined) headers['content-type'] = payload.contentType;
    let response: Response;
    try {
      response = await fetchImpl(path, {
        method,
        credentials: 'same-origin',
        headers,
        ...(payload === undefined ? {} : { body: payload.body }),
      });
    } catch {
      throw new ApiError('SERVICE_UNAVAILABLE', 0, 'Network error', []);
    }
    if (!response.ok) throw await failure(response);
    return response;
  }

  return {
    async request(method, path, schema, body) {
      const response = await call(method, path, jsonPayload(body));
      return schema.parse(await response.json());
    },
    async send(method, path, body) {
      await call(method, path, jsonPayload(body));
    },
    async upload(path, file, schema) {
      const contentType = file.type === '' ? 'application/octet-stream' : file.type;
      const response = await call('POST', path, { body: file, contentType });
      return schema.parse(await response.json());
    },
    async blob(path) {
      const response = await call('GET', path, undefined);
      return response.blob();
    },
  };
}

/** A path with its query string; empty and absent values are left out. */
export function withQuery(path: string, params: Readonly<Record<string, string | undefined>>) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, value);
  }
  const text = query.toString();
  return text === '' ? path : `${path}?${text}`;
}

/** Saves a downloaded file under a name, through a temporary link (works in every browser). */
export function saveFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  // Give the browser a moment to start the download before the address goes away.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 10_000);
}
